import type { MouseEvent, ReactNode } from 'react'

import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface LotListCardProps {
    /**
     * Mã lô element. Caller thường truyền `<Link to=... params=...>{code}</Link>`
     * để giữ type-safe route qua TanStack Router.
     */
    code: ReactNode
    /** Attribute chính: grade/material/product name. */
    attribute: string
    /** Attribute phụ: warehouse/grower/... (optional). */
    attributeSub?: string
    quantityAvailable: number
    quantityUnit?: string
    createdAt: string
    /** Actions bên phải header (print button, ...). */
    actions?: ReactNode
    /**
     * Tap anywhere trên card → navigate detail. Caller truyền () => navigate({...}).
     * Actions container tự stopPropagation nên tap Print không trigger navigate.
     */
    onClick?: () => void
}

export const LOT_CARD_CODE_CLASS =
    'font-mono text-sm font-medium text-primary hover:underline break-all'

/**
 * Card mobile hiển thị 1 lô trong danh sách.
 * Dùng chung cho 5 warehouse lot lists (sales/purchase/sorting/scenting/packaging).
 */
export function LotListCard({
    code,
    attribute,
    attributeSub,
    quantityAvailable,
    quantityUnit,
    createdAt,
    actions,
    onClick,
}: LotListCardProps) {
    const unit = quantityUnit ? ` ${quantityUnit}` : ''
    const clickable = !!onClick
    const stopPropagation = (e: MouseEvent) => e.stopPropagation()
    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            className={`rounded-md border bg-card p-3 space-y-2 ${
                clickable ? 'cursor-pointer hover:bg-muted/50' : ''
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="break-all" onClick={stopPropagation}>
                        {code}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5 break-words">
                        {attribute}
                        {attributeSub ? ` · ${attributeSub}` : ''}
                    </div>
                </div>
                {actions && (
                    <div
                        className="flex shrink-0 items-center gap-1"
                        onClick={stopPropagation}
                    >
                        {actions}
                    </div>
                )}
            </div>
            <div className="grid grid-cols-1 gap-2">
                <div className="rounded-md bg-primary/10 px-3 py-2">
                    <div className="text-xs text-muted-foreground">
                        {m.sl_lots_col_available()}
                    </div>
                    <div className="text-lg font-semibold tabular-nums text-primary">
                        {formatQty(quantityAvailable)}
                        {unit}
                    </div>
                </div>
            </div>
            <div className="text-xs text-muted-foreground">
                {formatDateTimeVN(createdAt)}
            </div>
        </div>
    )
}
